import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import FileUpload from '@/pages/newSupported/components/FileUpload';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import { EligibilityLimitOverrideDtoIn, EligibilityOverrideLimitType } from '@/models/in/EligibilityLimitOverrideDtoIn';
import { EligibilityLimitOverrideDtoOut } from '@/models/out/EligibilityLimitOverrideDtoOut';
import { ManagementSupportedDtoIn, SupportedsQueryParams } from '@/models/in/ManagementSupportedDtoIn';
import Fund from '@/models/Fund';
import { useGetAuthApi, usePostAuthApiInfiniteQuery } from '@/hooks/UseApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { postAuth, putAuth } from '@/api/ApiCalls';
import { FileReaderHelper } from '@/helpers/fileReaderHelper';
import addIcon from '@/assets/icons/add.svg';
import {
  getDefaultIsraelExpiresAt,
  israelTodayIso,
  parseExpiresAtFromServer,
} from '@/helpers/israelCalendarDate';

const QUERY_URL = 'management/eligibility-limit-overrides';
const SEARCH_DEBOUNCE_MS = 400;
const CONTACTS_PAGE_SIZE = 20;
const DEFAULT_EXPIRY_MONTHS = 18;
const DEFAULT_PERIOD_MONTHS = 3;

type OverrideType = 'contact' | 'fund';

interface SelectedEntity {
  id: number;
  label: string;
}

interface EditOrCreateOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  overrideToEdit?: EligibilityLimitOverrideDtoIn | null;
  onSuccess: () => void;
}

const EditOrCreateOverrideDialog = ({
  open,
  onClose,
  overrideToEdit,
  onSuccess,
}: EditOrCreateOverrideDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const [overrideType, setOverrideType] = useState<OverrideType>('contact');
  const [selectedEntities, setSelectedEntities] = useState<SelectedEntity[]>([]);
  // A contact override is always for one fund (HAT-1477)
  const [contactFund, setContactFund] = useState<SelectedEntity | null>(null);
  const [limitType, setLimitType] = useState<EligibilityOverrideLimitType>('Monthly');
  const [amount, setAmount] = useState('');
  const [startsAt, setStartsAt] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [doubleOnHoliday, setDoubleOnHoliday] = useState(true);
  const [comments, setComments] = useState('');
  const [files, setFiles] = useState<(File | null)[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Contact search state
  const [contactSearch, setContactSearch] = useState('');
  const [debouncedContactSearch] = useDebounceValue(contactSearch, SEARCH_DEBOUNCE_MS);
  // Fund search state
  const [fundSearch, setFundSearch] = useState('');

  const isEditing = !!overrideToEdit;
  // Only a contact override can be a period one
  const effectiveLimitType: EligibilityOverrideLimitType = overrideType === 'contact' ? limitType : 'Monthly';
  const isPeriod = effectiveLimitType === 'Period';

  // Load funds for fund selector
  const { data: funds, isLoading: isFundsLoading } = useGetAuthApi<Fund[]>('funds');

  const filteredFunds = useMemo(() => {
    if (!funds) return [];
    if (!fundSearch) return funds;
    return funds.filter((f) =>
      `${f.id} ${f.name}`.toLowerCase().includes(fundSearch.toLowerCase())
    );
  }, [funds, fundSearch]);

  // Load contacts - only send search term to server when 2+ chars typed, otherwise load default list
  const effectiveContactSearch = debouncedContactSearch.length >= 2 ? debouncedContactSearch : undefined;
  const contactsQueryParams: SupportedsQueryParams = useMemo(() => ({
    sortBy: 'ContactName',
    sortDirection: 'Asc',
    searchTerm: effectiveContactSearch,
  }), [effectiveContactSearch]);

  const { data: contactsResponse, isLoading: isContactsLoading, isFetching: isContactsFetching } = usePostAuthApiInfiniteQuery<ManagementSupportedDtoIn, SupportedsQueryParams>(
    overrideType === 'contact' ? 'management/supporteds' : '',
    contactsQueryParams,
    CONTACTS_PAGE_SIZE,
  );

  const pendingContactSearch = contactSearch.length >= 2 ? contactSearch : undefined;
  const isContactSearchStale = pendingContactSearch !== effectiveContactSearch || isContactsFetching;

  const contactResults = useMemo(
    () => contactsResponse?.pages.flatMap((page) => page.data) ?? [],
    [contactsResponse]
  );

  useEffect(() => {
    if (open) {
      if (overrideToEdit) {
        const type: OverrideType = overrideToEdit.contactId ? 'contact' : 'fund';
        setOverrideType(type);
        setSelectedEntities([{
          id: (overrideToEdit.contactId ?? overrideToEdit.fundId)!,
          label: (overrideToEdit.contactName ?? overrideToEdit.fundName) ?? '',
        }]);
        // A contact override from before HAT-1477 has no fund - the coordinator picks one on edit
        setContactFund(type === 'contact' && overrideToEdit.fundId
          ? { id: overrideToEdit.fundId, label: `${overrideToEdit.fundId} | ${overrideToEdit.fundName ?? ''}` }
          : null);
        setLimitType(overrideToEdit.limitType ?? 'Monthly');
        setAmount(String(overrideToEdit.monthlyLimitAmount));
        setStartsAt(parseExpiresAtFromServer(overrideToEdit.startsAt) || israelTodayIso());
        setExpiresAt(parseExpiresAtFromServer(overrideToEdit.expiresAt));
        setDoubleOnHoliday(overrideToEdit.doubleOnHoliday);
        setComments(overrideToEdit.comments ?? '');
      } else {
        setOverrideType('contact');
        setSelectedEntities([]);
        setContactFund(null);
        setLimitType('Monthly');
        setAmount('');
        setStartsAt(israelTodayIso());
        setExpiresAt(getDefaultIsraelExpiresAt(DEFAULT_EXPIRY_MONTHS));
        setDoubleOnHoliday(true);
        setComments('');
      }
      setFiles([]);
      setContactSearch('');
      setFundSearch('');
      setError(null);
    }
  }, [open, overrideToEdit]);

  const isEntitySelected = (id: number) => selectedEntities.some((e) => e.id === id);

  const toggleContact = (contact: ManagementSupportedDtoIn) => {
    if (isEntitySelected(contact.id)) {
      setSelectedEntities((prev) => prev.filter((e) => e.id !== contact.id));
    } else {
      setSelectedEntities((prev) => [...prev, {
        id: contact.id,
        label: `${contact.firstName} ${contact.lastName} (${contact.israeliId})`,
      }]);
    }
    if (error) setError(null);
  };

  const toggleFund = (fund: Fund) => {
    if (isEntitySelected(fund.id)) {
      setSelectedEntities((prev) => prev.filter((e) => e.id !== fund.id));
    } else {
      setSelectedEntities((prev) => [...prev, {
        id: fund.id,
        label: `${fund.id} | ${fund.name}`,
      }]);
    }
    if (error) setError(null);
  };

  const removeEntity = (id: number) => {
    setSelectedEntities((prev) => prev.filter((e) => e.id !== id));
  };

  const handleSubmit = async () => {
    if (selectedEntities.length === 0) {
      setError(t('eligibilityOverrides.dialog.invalidId'));
      return;
    }

    if (overrideType === 'contact' && !contactFund) {
      setError(t('eligibilityOverrides.dialog.invalidFund'));
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (!amount.trim() || isNaN(parsedAmount) || parsedAmount <= 0) {
      setError(t('eligibilityOverrides.dialog.invalidAmount'));
      return;
    }

    if (!expiresAt || !/^\d{4}-\d{2}-\d{2}$/.test(expiresAt)) {
      setError(t('eligibilityOverrides.dialog.invalidExpiresAt'));
      return;
    }
    // Rule: expiresAt must be today or later, compared on the Asia/Jerusalem calendar.
    // Both operands are yyyy-MM-dd on the Israel calendar, so a lexicographic
    // string compare is exactly the calendar comparison the backend runs.
    // On edit, if the user hasn't changed the Israel calendar day, skip the
    // past-date guard — the backend preserves the stored ExpiresAt in that case.
    const previousExpiresAt = overrideToEdit
      ? parseExpiresAtFromServer(overrideToEdit.expiresAt)
      : null;
    const dateChanged = !isEditing || expiresAt !== previousExpiresAt;
    if (dateChanged && expiresAt < israelTodayIso()) {
      setError(t('eligibilityOverrides.dialog.expiresAtInPast'));
      return;
    }

    if (isPeriod) {
      if (!startsAt || !/^\d{4}-\d{2}-\d{2}$/.test(startsAt)) {
        setError(t('eligibilityOverrides.dialog.invalidStartsAt'));
        return;
      }
      if (startsAt > expiresAt) {
        setError(t('eligibilityOverrides.dialog.startsAfterExpires'));
        return;
      }
    }

    setError(null);
    setIsSubmitting(true);

    const buildBody = (entityId: number, documents: EligibilityLimitOverrideDtoOut['documents']): EligibilityLimitOverrideDtoOut => ({
      contactId: overrideType === 'contact' ? entityId : null,
      fundId: overrideType === 'fund' ? entityId : contactFund!.id,
      monthlyLimitAmount: parsedAmount,
      limitType: effectiveLimitType,
      // A period total has no monthly limit for a holiday to double
      doubleOnHoliday: !isPeriod && doubleOnHoliday,
      startsAt: isPeriod ? startsAt : null,
      expiresAt,
      comments: comments.trim() || null,
      documents,
    });

    try {
      const validFiles = files.filter((f): f is File => f !== null);
      const documents = validFiles.length > 0
        ? (await Promise.all(validFiles.map((file) => FileReaderHelper.fileToDocumentDtoOut(file)))).flat()
        : undefined;

      if (isEditing) {
        const body = buildBody(selectedEntities[0].id, documents);
        await putAuth<EligibilityLimitOverrideDtoIn, object, EligibilityLimitOverrideDtoOut>(
          `${QUERY_URL}/${overrideToEdit!.id}`, headers, {}, body
        );
      } else {
        const errors: string[] = [];
        for (const entity of selectedEntities) {
          const body = buildBody(entity.id, documents);
          try {
            await postAuth<EligibilityLimitOverrideDtoIn, EligibilityLimitOverrideDtoOut>(QUERY_URL, headers, body);
          } catch (err: unknown) {
            const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '';
            const msg = axiosMsg || (err instanceof Error ? err.message : '');
            errors.push(`${entity.label}: ${msg.includes('already exists') ? t('eligibilityOverrides.dialog.duplicateError') : msg}`);
          }
        }
        if (errors.length > 0) {
          setError(errors.join('\n'));
          onSuccess();
          return;
        }
      }
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage.includes('already exists')) {
        setError(t('eligibilityOverrides.dialog.duplicateError'));
      } else {
        setError(t(isEditing ? 'eligibilityOverrides.dialog.errorUpdating' : 'eligibilityOverrides.dialog.errorCreating'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOverrideType('contact');
    setSelectedEntities([]);
    setContactFund(null);
    setLimitType('Monthly');
    setAmount('');
    setStartsAt('');
    setExpiresAt('');
    setDoubleOnHoliday(true);
    setComments('');
    setFiles([]);
    setContactSearch('');
    setFundSearch('');
    setError(null);
    onClose();
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) handleClose();
      }}
      title={isEditing
        ? t('eligibilityOverrides.dialog.editTitle')
        : t('eligibilityOverrides.dialog.createTitle')
      }
    >
      {/* Fixed width: the dialog centres its body, so a content-sized form jumped in width
          whenever a field, hint or chip appeared */}
      <div className="flex flex-col gap-4 p-4 w-[560px] max-w-full">
        {/* Override type selector */}
        <div className="flex flex-col gap-1">
          <label className="body-sm-semibold text-(--font-secondary)">
            {t('eligibilityOverrides.dialog.overrideType')}
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="overrideType"
                value="contact"
                checked={overrideType === 'contact'}
                onChange={() => {
                  setOverrideType('contact');
                  setSelectedEntities([]);
                  setContactSearch('');
                  setFundSearch('');
                  if (error) setError(null);
                }}
                disabled={isEditing}
              />
              <span className="body-sm-regular">{t('eligibilityOverrides.contact')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="overrideType"
                value="fund"
                checked={overrideType === 'fund'}
                onChange={() => {
                  setOverrideType('fund');
                  setSelectedEntities([]);
                  setContactSearch('');
                  setFundSearch('');
                  if (error) setError(null);
                }}
                disabled={isEditing}
              />
              <span className="body-sm-regular">{t('eligibilityOverrides.fund')}</span>
            </label>
          </div>
        </div>

        {/* Selected entities panel */}
        {selectedEntities.length > 0 && (
          <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="body-sm-semibold text-blue-800">
                {t('eligibilityOverrides.dialog.selectedCount', { count: selectedEntities.length })}
              </span>
              {!isEditing && selectedEntities.length > 1 && (
                <button
                  type="button"
                  className="body-sm-regular text-blue-600 hover:text-blue-800 underline"
                  onClick={() => setSelectedEntities([])}
                >
                  {t('eligibilityOverrides.dialog.clearAll')}
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedEntities.map((entity) => (
                <span
                  key={entity.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-blue-200 body-sm-regular shadow-sm"
                >
                  <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                  {entity.label}
                  {!isEditing && (
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-500 text-lg leading-none ms-1 transition-colors"
                      onClick={() => removeEntity(entity.id)}
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Entity selection */}
        {!isEditing && (
          <div className="flex flex-col gap-1">
            <label className="body-sm-semibold text-(--font-secondary)">
              {overrideType === 'contact'
                ? t('eligibilityOverrides.dialog.selectContact')
                : t('eligibilityOverrides.dialog.selectFund')
              }
            </label>

            {overrideType === 'fund' ? (
              // Fund list with checkboxes
              <div className="flex flex-col gap-1">
                <input
                  type="text"
                  className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                  value={fundSearch}
                  onChange={(e) => setFundSearch(e.target.value)}
                  placeholder={t('eligibilityOverrides.dialog.fundSearchPlaceholder')}
                />
                <div className="border border-(--border-main) rounded-lg max-h-[180px] overflow-y-auto">
                  {isFundsLoading ? (
                    <div className="px-3 py-2 body-sm-regular text-(--font-secondary)">
                      {t('eligibilityOverrides.dialog.loading')}
                    </div>
                  ) : filteredFunds.length === 0 ? (
                    <div className="px-3 py-2 body-sm-regular text-(--font-secondary)">
                      {t('eligibilityOverrides.dialog.noFundsFound')}
                    </div>
                  ) : (
                    filteredFunds.map((fund) => (
                      <label
                        key={fund.id}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-(--surface-secondary-lowest) cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isEntitySelected(fund.id)}
                          onChange={() => toggleFund(fund)}
                        />
                        <span className="body-sm-semibold">{fund.id}</span>
                        <span className="body-sm-regular">{fund.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            ) : (
              // Contact list with checkboxes (same layout as funds)
              <div className="flex flex-col gap-1">
                <input
                  type="text"
                  className="w-full border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                  value={contactSearch}
                  onChange={(e) => {
                    setContactSearch(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder={t('eligibilityOverrides.dialog.contactSearchPlaceholder')}
                />
                <div className="relative border border-(--border-main) rounded-lg max-h-[180px] overflow-y-scroll">
                  {isContactSearchStale && (
                    <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
                      <div className="w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {isContactsLoading ? (
                    <div className="px-3 py-2 body-sm-regular text-(--font-secondary)">
                      {t('eligibilityOverrides.dialog.loading')}
                    </div>
                  ) : contactResults.length === 0 ? (
                    <div className="px-3 py-2 body-sm-regular text-(--font-secondary)">
                      {t('eligibilityOverrides.dialog.noContactsFound')}
                    </div>
                  ) : (
                    contactResults.map((contact) => (
                      <label
                        key={contact.id}
                        className={`flex items-center gap-2 px-3 py-2 hover:bg-(--surface-secondary-lowest) cursor-pointer ${isEntitySelected(contact.id) ? 'bg-blue-50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isEntitySelected(contact.id)}
                          onChange={() => toggleContact(contact)}
                        />
                        <span className="body-sm-semibold">{contact.firstName} {contact.lastName}</span>
                        <span className="text-(--font-secondary) body-sm-regular">({contact.israeliId})</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fund of a contact override */}
        {overrideType === 'contact' && (
          <div className="flex flex-col gap-1">
            <label className="body-sm-semibold text-(--font-secondary)">
              {t('eligibilityOverrides.dialog.contactFund')}
            </label>
            <span className="body-xs-regular text-(--font-secondary)">
              {t('eligibilityOverrides.dialog.contactFundHint')}
            </span>
            {contactFund ? (
              <span className="inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-lg bg-white border border-blue-200 body-sm-regular shadow-sm">
                <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                {contactFund.label}
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-500 text-lg leading-none ms-1 transition-colors"
                  onClick={() => setContactFund(null)}
                >
                  ×
                </button>
              </span>
            ) : (
              <div className="flex flex-col gap-1">
                <input
                  type="text"
                  className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                  value={fundSearch}
                  onChange={(e) => setFundSearch(e.target.value)}
                  placeholder={t('eligibilityOverrides.dialog.fundSearchPlaceholder')}
                />
                <div className="border border-(--border-main) rounded-lg max-h-[180px] overflow-y-auto">
                  {isFundsLoading ? (
                    <div className="px-3 py-2 body-sm-regular text-(--font-secondary)">
                      {t('eligibilityOverrides.dialog.loading')}
                    </div>
                  ) : filteredFunds.length === 0 ? (
                    <div className="px-3 py-2 body-sm-regular text-(--font-secondary)">
                      {t('eligibilityOverrides.dialog.noFundsFound')}
                    </div>
                  ) : (
                    filteredFunds.map((fund) => (
                      <button
                        key={fund.id}
                        type="button"
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-(--surface-secondary-lowest) text-start"
                        onClick={() => {
                          setContactFund({ id: fund.id, label: `${fund.id} | ${fund.name}` });
                          setFundSearch('');
                          if (error) setError(null);
                        }}
                      >
                        <span className="body-sm-semibold">{fund.id}</span>
                        <span className="body-sm-regular">{fund.name}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Monthly limit or a total for a period - contact overrides only */}
        {overrideType === 'contact' && (
          <div className="flex flex-col gap-1">
            <label className="body-sm-semibold text-(--font-secondary)">
              {t('eligibilityOverrides.dialog.limitType')}
            </label>
            <div className="flex gap-4">
              {(['Monthly', 'Period'] as const).map((type) => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="limitType"
                    value={type}
                    checked={limitType === type}
                    onChange={() => {
                      setLimitType(type);
                      // A new override gets the default length of its type
                      if (!isEditing) {
                        setExpiresAt(getDefaultIsraelExpiresAt(type === 'Period' ? DEFAULT_PERIOD_MONTHS : DEFAULT_EXPIRY_MONTHS));
                      }
                      if (error) setError(null);
                    }}
                  />
                  <span className="body-sm-regular">
                    {t(type === 'Period'
                      ? 'eligibilityOverrides.dialog.limitTypePeriod'
                      : 'eligibilityOverrides.dialog.limitTypeMonthly')}
                  </span>
                </label>
              ))}
            </div>
            {isPeriod && (
              <span className="body-xs-regular text-(--font-secondary)">
                {t('eligibilityOverrides.dialog.periodHint')}
              </span>
            )}
          </div>
        )}

        {/* Amount & Duration */}
        <div className="flex gap-4">
          <div className="flex flex-col gap-1 flex-1">
            <label htmlFor="override-amount" className="body-sm-semibold text-(--font-secondary)">
              {t(isPeriod ? 'eligibilityOverrides.dialog.periodAmount' : 'eligibilityOverrides.dialog.amount')}
            </label>
            <input
              id="override-amount"
              type="number"
              className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                if (error) setError(null);
              }}
              placeholder={t('eligibilityOverrides.dialog.amountPlaceholder')}
              dir="ltr"
              min="0"
            />
          </div>
          {isPeriod && (
            <div className="flex flex-col gap-1 flex-1">
              <label htmlFor="override-starts-at" className="body-sm-semibold text-(--font-secondary)">
                {t('eligibilityOverrides.dialog.startsAt')}
              </label>
              <input
                id="override-starts-at"
                type="date"
                className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
                value={startsAt}
                max={expiresAt || undefined}
                onChange={(e) => {
                  setStartsAt(e.target.value);
                  if (error) setError(null);
                }}
                dir="ltr"
              />
            </div>
          )}
          <div className="flex flex-col gap-1 flex-1">
            <label htmlFor="override-expires-at" className="body-sm-semibold text-(--font-secondary)">
              {t('eligibilityOverrides.dialog.expiresAt')}
            </label>
            <input
              id="override-expires-at"
              type="date"
              className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
              value={expiresAt}
              min={israelTodayIso()}
              onChange={(e) => {
                setExpiresAt(e.target.value);
                if (error) setError(null);
              }}
              dir="ltr"
            />
          </div>
        </div>

        {/* Include Holiday Bonus - a period total has no monthly limit to double */}
        {!isPeriod && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={doubleOnHoliday}
              onChange={(e) => setDoubleOnHoliday(e.target.checked)}
            />
            <span className="body-sm-semibold text-(--font-secondary)">
              {t('eligibilityOverrides.dialog.doubleOnHoliday')}
            </span>
          </label>
        )}

        {/* Comments */}
        <div className="flex flex-col gap-1">
          <label htmlFor="override-comments" className="body-sm-semibold text-(--font-secondary)">
            {t('eligibilityOverrides.dialog.comments')}
          </label>
          <textarea
            id="override-comments"
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular resize-none"
            rows={3}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder={t('eligibilityOverrides.dialog.commentsPlaceholder')}
          />
        </div>

        {/* File attachments */}
        <div className="flex flex-col gap-2">
          <label className="body-sm-semibold text-(--font-secondary)">
            {t('eligibilityOverrides.dialog.attachments')}
          </label>

          {/* Existing documents (in edit mode) */}
          {isEditing && overrideToEdit?.documents && overrideToEdit.documents.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="body-sm-regular text-(--font-secondary)">
                {t('eligibilityOverrides.dialog.existingFiles')}
              </span>
              <div className="flex flex-col gap-1">
                {overrideToEdit.documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <a
                      href={doc.uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="body-sm-regular text-blue-600 hover:text-blue-800 underline truncate"
                    >
                      {doc.name}
                    </a>
                    <DownloadFileButton uri={doc.uri} fileName={doc.name} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New file upload slots */}
          {files.map((file, index) => (
            <FileUpload
              key={index}
              placeholder={t('eligibilityOverrides.dialog.attachments')}
              value={file}
              onChange={(newFile) => {
                setFiles((prev) => {
                  const updated = [...prev];
                  updated[index] = newFile;
                  return updated;
                });
              }}
              customDescription={{
                description: file?.name ?? '',
                onDescriptionChange: () => {},
                onRemove: () => {
                  setFiles((prev) => prev.filter((_, i) => i !== index));
                },
              }}
            />
          ))}

          <button
            type="button"
            onClick={() => setFiles((prev) => [...prev, null])}
            className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
          >
            <img src={addIcon} alt="" />
            {t('eligibilityOverrides.dialog.addFile')}
          </button>
        </div>

        {error && (
          <p className="text-red-500 body-sm-regular whitespace-pre-line">{error}</p>
        )}

        <div className="flex gap-3 justify-end mt-2">
          <Button
            variant="white"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('close')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || selectedEntities.length === 0}
          >
            {isSubmitting
              ? t('saving')
              : selectedEntities.length > 1
                ? t('eligibilityOverrides.dialog.saveMultiple', { count: selectedEntities.length })
                : t('save')
            }
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditOrCreateOverrideDialog;
